import { config } from '@/config';

export type EntitlementBootstrapResult = {
  hubspotCustomerId: string | null;
  enabled: boolean;
  changed: boolean;
  tokenRefreshRequired: boolean;
  disabledReason: string | null;
  reasonCodes: string[];
};

export class EntitlementBootstrapError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'EntitlementBootstrapError';
    this.status = status;
  }
}

const trimSlashes = (value: string) => value.replace(/\/+$/, '');

export async function refreshHubSpotEntitlements(accessToken: string): Promise<EntitlementBootstrapResult | null> {
  const aclBaseUrl = config().aclBaseUrl?.trim();
  if (!aclBaseUrl) {
    return null;
  }

  const response = await fetch(`${trimSlashes(aclBaseUrl)}/auth/entitlements/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const payload = await response.json().catch(() => ({})) as Partial<EntitlementBootstrapResult> & { message?: string };
  if (!response.ok) {
    throw new EntitlementBootstrapError(payload.message ?? 'Account entitlements could not be refreshed', response.status);
  }

  return {
    hubspotCustomerId: typeof payload.hubspotCustomerId === 'string'
      ? payload.hubspotCustomerId
      : null,
    enabled: payload.enabled !== false,
    changed: payload.changed === true,
    tokenRefreshRequired: payload.tokenRefreshRequired === true,
    disabledReason: typeof payload.disabledReason === 'string' ? payload.disabledReason : null,
    reasonCodes: Array.isArray(payload.reasonCodes)
      ? payload.reasonCodes.filter((entry): entry is string => typeof entry === 'string')
      : [],
  };
}
